import firebase from "firebase/compat/app";
import { db } from "services/firebase";
import { LLM_COLLECTIONS } from "./llmConstants";
import { getHierarchyPromptDocId } from "./hierarchyPromptScope";

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  return 0;
};

const SCOPES = {
  GLOBAL: "global",
  REGION: "region",
  ORG: "org",
  HIERARCHY: "hierarchy",
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

/** Compare stored snapshot values for history rows and the prompt history UI. */
export function historyFieldValuesEqual(field, before, after) {
  if (field === "maxTokens") {
    return Number(before ?? 0) === Number(after ?? 0);
  }
  if (field === "temperature") {
    const b = typeof before === "number" ? before : Number.NaN;
    const a = typeof after === "number" ? after : Number.NaN;
    if (Number.isNaN(b) && Number.isNaN(a)) return true;
    if (Number.isNaN(b) || Number.isNaN(a)) return false;
    return b === a;
  }
  const bs = before == null ? "" : String(before);
  const as = after == null ? "" : String(after);
  return bs === as;
}

const getPreviousSnapshotForHistory = (previousData, field) => {
  if (hasOwn(previousData, field)) return previousData[field];
  if (field === "systemPrompt" && hasOwn(previousData, "globalPrompt")) {
    return previousData.globalPrompt;
  }
  return null;
};

const pickActuallyChangedValues = (changedValues, previousData) => {
  const out = {};
  for (const [key, nextVal] of Object.entries(changedValues || {})) {
    const prevVal = getPreviousSnapshotForHistory(previousData, key);
    if (!historyFieldValuesEqual(key, prevVal, nextVal)) {
      out[key] = nextVal;
    }
  }
  return out;
};

const buildHistoryPayload = ({
  changedValues,
  previousData = {},
  scope,
  scopeId,
  promptId,
  reason = null,
}) => {
  const actualChanges = pickActuallyChangedValues(changedValues, previousData);
  const changedFields = Object.keys(actualChanges);
  if (changedFields.length === 0) return null;

  const previousValues = changedFields.reduce((acc, field) => {
    acc[field] = getPreviousSnapshotForHistory(previousData, field);
    return acc;
  }, {});

  return {
    ...actualChanges,
    changedFields,
    previousValues,
    nextValues: { ...actualChanges },
    scope,
    scopeId,
    promptId,
    reason: typeof reason === "string" ? reason.trim() || null : null,
  };
};

const getLocalPromptDocId = ({ scope, scopeId, promptId }) =>
  `${scope}__${scopeId}__${promptId}`;

const getLocalPromptRef = ({ scope, scopeId, promptId }) =>
  db
    .collection(LLM_COLLECTIONS.LOCAL_PROMPTS)
    .doc(getLocalPromptDocId({ scope, scopeId, promptId }));

const getLocalPromptPath = ({ scope, scopeId, promptId }) =>
  `${LLM_COLLECTIONS.LOCAL_PROMPTS}/${getLocalPromptDocId({
    scope,
    scopeId,
    promptId,
  })}`;

const getHierarchyPromptRef = ({ level, scopeId, promptId }) =>
  db
    .collection(LLM_COLLECTIONS.HIERARCHY_PROMPTS)
    .doc(getHierarchyPromptDocId({ level, scopeId, promptId }));

const extractHierarchyPromptText = (layer = {}) => {
  const prompt = typeof layer.prompt === "string" ? layer.prompt.trim() : "";
  const organizationPrompt =
    typeof layer.organizationPrompt === "string"
      ? layer.organizationPrompt.trim()
      : "";
  return prompt || organizationPrompt || "";
};

const normalizePrompt = (
  id,
  queryData = {},
  organizationLayer = {},
  globalLayer = {},
  regionalLayer = {},
  p4Layer = {},
  p5Layer = {},
  p6Layer = {},
) => ({
  id,
  featureName: queryData.featureName || id,
  featureDescription: queryData.featureDescription || "",
  category: queryData.category || "unknown",
  contextSummary: queryData.contextSummary || "",
  prompt:
    organizationLayer.organizationPrompt || organizationLayer.prompt || "",
  organizationPrompt:
    organizationLayer.organizationPrompt || organizationLayer.prompt || "",
  regionalPrompt: regionalLayer.regionalPrompt || regionalLayer.prompt || "",
  subInstitutionPrompt: extractHierarchyPromptText(p4Layer),
  departmentPrompt: extractHierarchyPromptText(p5Layer),
  subDepartmentPrompt: extractHierarchyPromptText(p6Layer),
  p4LayerMeta: p4Layer.level ? { level: 4, scopeId: p4Layer.scopeId || null } : null,
  p5LayerMeta: p5Layer.level ? { level: 5, scopeId: p5Layer.scopeId || null } : null,
  p6LayerMeta: p6Layer.level ? { level: 6, scopeId: p6Layer.scopeId || null } : null,
  systemPrompt: globalLayer.systemPrompt || globalLayer.globalPrompt || "",
  reviewStatus:
    typeof globalLayer.reviewStatus === "string" ? globalLayer.reviewStatus : null,
  responseFormat: globalLayer.responseFormat || "",
  contextProvided: globalLayer.contextProvided || "",
  testInputExample: queryData.testInputExample || "",
  model: globalLayer.model || "",
  maxTokens: Number(globalLayer.maxTokens || 0),
  temperature:
    typeof globalLayer.temperature === "number" ? globalLayer.temperature : 0.7,
  prioritizeUploadedDocs: globalLayer.prioritizeUploadedDocs === true,
  // Org-layer override; null when the org has not set it (global value applies).
  orgPrioritizeUploadedDocs:
    typeof organizationLayer.prioritizeUploadedDocs === "boolean"
      ? organizationLayer.prioritizeUploadedDocs
      : null,
  layerOverrideMode:
    organizationLayer.layerOverrideMode === "exclusive"
      ? "exclusive"
      : organizationLayer.layerOverrideMode === "additive"
        ? "additive"
        : globalLayer.layerOverrideMode === "exclusive"
          ? "exclusive"
          : "additive",
  exclusiveReferenceMode:
    organizationLayer.exclusiveReferenceMode === true ||
    globalLayer.exclusiveReferenceMode === true,
  orgModel: organizationLayer.model || null,
  orgProvider: organizationLayer.provider || null,
  orgTemperature:
    typeof organizationLayer.temperature === "number"
      ? organizationLayer.temperature
      : null,
  orgMaxTokens:
    typeof organizationLayer.maxTokens === "number"
      ? organizationLayer.maxTokens
      : null,
  source: "firestore",
  chainConfig: queryData.chainConfig || null,
  updatedAt:
    organizationLayer.updatedAt ||
    globalLayer.updatedAt ||
    queryData.updatedAt ||
    null,
  updatedBy:
    organizationLayer.updatedBy ||
    globalLayer.updatedBy ||
    queryData.updatedBy ||
    null,
  createdAt:
    organizationLayer.createdAt ||
    globalLayer.createdAt ||
    queryData.createdAt ||
    null,
  createdBy:
    organizationLayer.createdBy ||
    globalLayer.createdBy ||
    queryData.createdBy ||
    null,
});

const getSnapshotOrEmpty = async (promise, source) => {
  try {
    return await promise;
  } catch (error) {
    console.warn("[promptRegistryService] Optional prompt layer denied", {
      source,
      code: error?.code,
      message: error?.message,
    });
    return { docs: [] };
  }
};

const getDocOrEmptyOnPermissionDenied = async (promise, source) => {
  try {
    return await promise;
  } catch (error) {
    if (error?.code === "permission-denied") {
      console.warn("[promptRegistryService] Optional prompt doc denied", {
        source,
        code: error?.code,
        message: error?.message,
      });
      return { exists: false, data: () => ({}), __permissionDenied: true };
    }
    throw error;
  }
};

const listAll = async ({
  organizationId = null,
  regionId = null,
  isGlobalAdmin = false,
  p4ScopeId = null,
  p5ScopeId = null,
  p6ScopeId = null,
} = {}) => {
  let queryConfigSnapshot;
  try {
    queryConfigSnapshot = await db.collection(LLM_COLLECTIONS.QUERY_CONFIG).get();
  } catch (error) {
    error.promptRegistrySource = LLM_COLLECTIONS.QUERY_CONFIG;
    throw error;
  }

  const regionalQuery =
    regionId && String(regionId).trim()
      ? getSnapshotOrEmpty(
          db
            .collection(LLM_COLLECTIONS.LOCAL_PROMPTS)
            .where("scope", "==", SCOPES.REGION)
            .where("scopeId", "==", String(regionId).trim())
            .get(),
          `${LLM_COLLECTIONS.LOCAL_PROMPTS}:region:${regionId}`,
        )
      : Promise.resolve({ docs: [] });

  const hierarchyQueries = [];
  if (p4ScopeId) {
    hierarchyQueries.push(
      getSnapshotOrEmpty(
        db
          .collection(LLM_COLLECTIONS.HIERARCHY_PROMPTS)
          .where("level", "==", 4)
          .where("scopeId", "==", p4ScopeId)
          .get(),
        `${LLM_COLLECTIONS.HIERARCHY_PROMPTS}:4:${p4ScopeId}`,
      ),
    );
  } else {
    hierarchyQueries.push(Promise.resolve({ docs: [] }));
  }
  if (p5ScopeId) {
    hierarchyQueries.push(
      getSnapshotOrEmpty(
        db
          .collection(LLM_COLLECTIONS.HIERARCHY_PROMPTS)
          .where("level", "==", 5)
          .where("scopeId", "==", p5ScopeId)
          .get(),
        `${LLM_COLLECTIONS.HIERARCHY_PROMPTS}:5:${p5ScopeId}`,
      ),
    );
  } else {
    hierarchyQueries.push(Promise.resolve({ docs: [] }));
  }
  if (p6ScopeId) {
    hierarchyQueries.push(
      getSnapshotOrEmpty(
        db
          .collection(LLM_COLLECTIONS.HIERARCHY_PROMPTS)
          .where("level", "==", 6)
          .where("scopeId", "==", p6ScopeId)
          .get(),
        `${LLM_COLLECTIONS.HIERARCHY_PROMPTS}:6:${p6ScopeId}`,
      ),
    );
  } else {
    hierarchyQueries.push(Promise.resolve({ docs: [] }));
  }

  const [
    orgPromptSnapshot,
    globalPromptSnapshot,
    regionalPromptSnapshot,
    p4PromptSnapshot,
    p5PromptSnapshot,
    p6PromptSnapshot,
  ] = await Promise.all([
    organizationId
      ? getSnapshotOrEmpty(
          db
            .collection(LLM_COLLECTIONS.LOCAL_PROMPTS)
            .where("scope", "==", SCOPES.ORG)
            .where("scopeId", "==", organizationId)
            .get(),
          `${LLM_COLLECTIONS.LOCAL_PROMPTS}:org:${organizationId}`,
        )
      : Promise.resolve({ docs: [] }),
    isGlobalAdmin
      ? getSnapshotOrEmpty(
          db.collection(LLM_COLLECTIONS.GLOBAL_PROMPTS).get(),
          LLM_COLLECTIONS.GLOBAL_PROMPTS,
        )
      : Promise.resolve({ docs: [] }),
    regionalQuery,
    ...hierarchyQueries,
  ]);

  const queryMap = new Map(
    queryConfigSnapshot.docs.map((doc) => [doc.id, doc.data()]),
  );
  const orgMap = new Map(
    orgPromptSnapshot.docs.map((doc) => [
      doc.data()?.promptId || doc.id,
      doc.data(),
    ]),
  );
  const globalMap = new Map(
    globalPromptSnapshot.docs.map((doc) => [doc.id, doc.data()]),
  );
  const regionalMap = new Map(
    regionalPromptSnapshot.docs.map((doc) => [
      doc.data()?.promptId || doc.id,
      doc.data(),
    ]),
  );
  const p4Map = new Map(
    p4PromptSnapshot.docs.map((doc) => [
      doc.data()?.promptId || doc.id.split("__").pop(),
      doc.data(),
    ]),
  );
  const p5Map = new Map(
    p5PromptSnapshot.docs.map((doc) => [
      doc.data()?.promptId || doc.id.split("__").pop(),
      doc.data(),
    ]),
  );
  const p6Map = new Map(
    p6PromptSnapshot.docs.map((doc) => [
      doc.data()?.promptId || doc.id.split("__").pop(),
      doc.data(),
    ]),
  );

  const promptIds = new Set([
    ...Array.from(queryMap.keys()),
    ...Array.from(orgMap.keys()),
    ...Array.from(globalMap.keys()),
    ...Array.from(regionalMap.keys()),
    ...Array.from(p4Map.keys()),
    ...Array.from(p5Map.keys()),
    ...Array.from(p6Map.keys()),
  ]);

  return Array.from(promptIds).map((id) =>
    normalizePrompt(
      id,
      queryMap.get(id),
      orgMap.get(id),
      globalMap.get(id),
      regionalMap.get(id),
      p4Map.get(id),
      p5Map.get(id),
      p6Map.get(id),
    ),
  );
};

const upsertPrompt = async ({
  promptId,
  updates,
  reason = null,
  actorUid,
  actorEmail = null,
  organizationId = null,
  regionId = null,
  isGlobalAdmin = false,
  p4ScopeId = null,
  p5ScopeId = null,
  p6ScopeId = null,
  canEditP4 = false,
  canEditP5 = false,
  canEditP6 = false,
  hierarchyInstitutionId = null,
}) => {
  const queryRef = db.collection(LLM_COLLECTIONS.QUERY_CONFIG).doc(promptId);
  const globalRef = db.collection(LLM_COLLECTIONS.GLOBAL_PROMPTS).doc(promptId);
  const orgPromptRef = organizationId
    ? getLocalPromptRef({
        scope: SCOPES.ORG,
        scopeId: organizationId,
        promptId,
      })
    : null;
  const trimmedRegionId = regionId && String(regionId).trim();
  const regionalPromptRef = trimmedRegionId
    ? getLocalPromptRef({
        scope: SCOPES.REGION,
        scopeId: trimmedRegionId,
        promptId,
      })
    : null;

  const [existingQuery, existingGlobal, existingOrgPrompt, existingRegional] =
    await Promise.all([
      queryRef.get(),
      isGlobalAdmin
        ? globalRef.get()
        : Promise.resolve({ exists: false, data: () => ({}) }),
      orgPromptRef
        ? getDocOrEmptyOnPermissionDenied(
            orgPromptRef.get(),
            `${LLM_COLLECTIONS.LOCAL_PROMPTS}:doc:${organizationId}:${promptId}`,
          )
        : Promise.resolve({ exists: false, data: () => ({}) }),
      regionalPromptRef
        ? getDocOrEmptyOnPermissionDenied(
            regionalPromptRef.get(),
            `${LLM_COLLECTIONS.LOCAL_PROMPTS}:region:${trimmedRegionId}:${promptId}`,
          )
        : Promise.resolve({ exists: false, data: () => ({}) }),
    ]);

  const writeOps = [];
  const now = firebase.firestore.FieldValue.serverTimestamp();
  let callerUserDocDebug = null;

  if (actorUid) {
    try {
      const callerUserDoc = await db.collection("users").doc(actorUid).get();
      callerUserDocDebug = callerUserDoc.exists
        ? {
            exists: true,
            role: callerUserDoc.data()?.role || null,
            organizationId: callerUserDoc.data()?.organizationId || null,
            admin: callerUserDoc.data()?.admin === true,
          }
        : { exists: false };
    } catch (error) {
      callerUserDocDebug = {
        exists: null,
        errorCode: error?.code || "unknown",
        errorMessage: error?.message || "Failed to read users/{uid}",
      };
    }
  }
  const organizationPrompt =
    typeof updates.organizationPrompt === "string"
      ? updates.organizationPrompt
      : typeof updates.prompt === "string"
        ? updates.prompt
        : undefined;

  const regionalPrompt =
    typeof updates.regionalPrompt === "string"
      ? updates.regionalPrompt
      : undefined;

  if (isGlobalAdmin) {
    const queryPayload = {};
    if (typeof updates.featureName === "string") {
      queryPayload.featureName = updates.featureName;
    }
    if (typeof updates.featureDescription === "string") {
      queryPayload.featureDescription = updates.featureDescription;
    }
    if (typeof updates.category === "string") {
      queryPayload.category = updates.category;
    }
    if (typeof updates.contextSummary === "string") {
      queryPayload.contextSummary = updates.contextSummary;
    }
    if (typeof updates.testInputExample === "string") {
      queryPayload.testInputExample = updates.testInputExample;
    }
    if (updates.chainConfig && typeof updates.chainConfig === "object") {
      queryPayload.chainConfig = updates.chainConfig;
    }
    if (Object.keys(queryPayload).length > 0 || !existingQuery.exists) {
      writeOps.push({
        source: `${LLM_COLLECTIONS.QUERY_CONFIG}:${promptId}`,
        debug: {
          action: existingQuery.exists ? "update" : "create",
          path: `${LLM_COLLECTIONS.QUERY_CONFIG}/${promptId}`,
          payload: {
            ...queryPayload,
            id: promptId,
            updatedBy: actorUid || null,
            ...(existingQuery.exists
              ? {}
              : { createdBy: actorUid || null }),
          },
        },
        run: () =>
          queryRef.set(
            {
              ...queryPayload,
              id: promptId,
              updatedBy: actorUid || null,
              updatedAt: now,
              ...(existingQuery.exists
                ? {}
                : { createdBy: actorUid || null, createdAt: now }),
            },
            { merge: true },
          ),
      });
    }

    // D-CM7-07-01: reviewStatus is not copied. Saving P1 text does not approve a combination.
    const globalPayload = {};
    if (typeof updates.systemPrompt === "string") {
      globalPayload.systemPrompt = updates.systemPrompt;
    }
    if (typeof updates.contextProvided === "string") {
      globalPayload.contextProvided = updates.contextProvided;
    }
    if (typeof updates.responseFormat === "string") {
      globalPayload.responseFormat = updates.responseFormat;
    }
    if (typeof updates.model === "string") {
      globalPayload.model = updates.model;
    }
    if (typeof updates.maxTokens === "number") {
      globalPayload.maxTokens = updates.maxTokens;
    }
    if (typeof updates.temperature === "number") {
      globalPayload.temperature = updates.temperature;
    }
    if (typeof updates.prioritizeUploadedDocs === "boolean") {
      globalPayload.prioritizeUploadedDocs = updates.prioritizeUploadedDocs;
    }
    if (
      updates.layerOverrideMode === "additive" ||
      updates.layerOverrideMode === "exclusive"
    ) {
      globalPayload.layerOverrideMode = updates.layerOverrideMode;
    }
    if (typeof updates.exclusiveReferenceMode === "boolean") {
      globalPayload.exclusiveReferenceMode = updates.exclusiveReferenceMode;
    }
    if (typeof updates.provider === "string") {
      globalPayload.provider = updates.provider;
    }

    if (Object.keys(globalPayload).length > 0 || !existingGlobal.exists) {
      writeOps.push({
        ref: globalRef,
        historyPayload: buildHistoryPayload({
          changedValues: globalPayload,
          previousData: existingGlobal.exists ? existingGlobal.data() : {},
          scope: SCOPES.GLOBAL,
          scopeId: "default",
          promptId,
          reason,
        }),
        source: `${LLM_COLLECTIONS.GLOBAL_PROMPTS}:${promptId}`,
        debug: {
          action: existingGlobal.exists ? "update" : "create",
          path: `${LLM_COLLECTIONS.GLOBAL_PROMPTS}/${promptId}`,
          payload: {
            ...globalPayload,
            scope: SCOPES.GLOBAL,
            scopeId: "default",
            promptId,
            updatedBy: actorUid || null,
            ...(existingGlobal.exists
              ? {}
              : { createdBy: actorUid || null }),
          },
        },
        run: () =>
          globalRef.set(
            {
              ...globalPayload,
              scope: SCOPES.GLOBAL,
              scopeId: "default",
              promptId,
              updatedBy: actorUid || null,
              updatedAt: now,
              ...(existingGlobal.exists
                ? {}
                : { createdBy: actorUid || null, createdAt: now }),
            },
            { merge: true },
          ),
      });
    }
  }

  // Org admins store prioritizeUploadedDocs on their org layer (they cannot
  // write llmGlobalPrompts). The org value overrides the global one at runtime.
  const orgPrioritizeUploadedDocs =
    !isGlobalAdmin && typeof updates.prioritizeUploadedDocs === "boolean"
      ? updates.prioritizeUploadedDocs
      : undefined;
  const orgPromptFields =
    typeof organizationPrompt === "string"
      ? { prompt: organizationPrompt, organizationPrompt }
      : {};
  const orgExtraFields =
    orgPrioritizeUploadedDocs !== undefined
      ? { prioritizeUploadedDocs: orgPrioritizeUploadedDocs }
      : {};
  const p1Version =
    Number(existingGlobal.data()?.p1Version) ||
    Number(existingQuery.data()?.p1Version) ||
    1;
  if (typeof updates.compositionMode === "string") {
    orgExtraFields.compositionMode = updates.compositionMode;
  }
  // ASI / Interaction Manager: model + layer mode editable below P1 (PROMPT 2)
  if (typeof updates.model === "string" && !isGlobalAdmin) {
    orgExtraFields.model = updates.model;
  }
  if (typeof updates.provider === "string" && !isGlobalAdmin) {
    orgExtraFields.provider = updates.provider;
  }
  if (typeof updates.temperature === "number" && !isGlobalAdmin) {
    orgExtraFields.temperature = updates.temperature;
  }
  if (typeof updates.maxTokens === "number" && !isGlobalAdmin) {
    orgExtraFields.maxTokens = updates.maxTokens;
  }
  if (
    (updates.layerOverrideMode === "additive" ||
      updates.layerOverrideMode === "exclusive") &&
    !isGlobalAdmin
  ) {
    orgExtraFields.layerOverrideMode = updates.layerOverrideMode;
  }
  if (typeof updates.exclusiveReferenceMode === "boolean" && !isGlobalAdmin) {
    orgExtraFields.exclusiveReferenceMode = updates.exclusiveReferenceMode;
  }
  if (Object.keys(orgPromptFields).length > 0 || Object.keys(orgExtraFields).length > 0) {
    if (Object.keys(orgPromptFields).length > 0 || orgPrioritizeUploadedDocs !== undefined) {
      orgExtraFields.p1VersionWrittenAgainst =
        updates.compositionMode === "substitutionary" ? null : p1Version;
    }
  }
  const orgChangedValues = { ...orgPromptFields, ...orgExtraFields };
  delete orgChangedValues.prompt; // history tracks organizationPrompt, not its alias

  if (orgPromptRef && Object.keys(orgChangedValues).length > 0) {
    writeOps.push({
      ref: orgPromptRef,
      historyPayload: buildHistoryPayload({
        changedValues: orgChangedValues,
        previousData: existingOrgPrompt.exists ? existingOrgPrompt.data() : {},
        scope: SCOPES.ORG,
        scopeId: organizationId,
        promptId,
        reason,
      }),
      source: `${LLM_COLLECTIONS.LOCAL_PROMPTS}:${SCOPES.ORG}:${organizationId}:${promptId}`,
      debug: {
        action: existingOrgPrompt.exists
          ? "update"
          : existingOrgPrompt.__permissionDenied
            ? "create-or-update (pre-read denied)"
            : "create",
        path: getLocalPromptPath({
          scope: SCOPES.ORG,
          scopeId: organizationId,
          promptId,
        }),
        callerUserDoc: callerUserDocDebug,
        payload: {
          scope: SCOPES.ORG,
          scopeId: organizationId,
          promptId,
          layer: "organization",
          ...orgPromptFields,
          ...orgExtraFields,
          updatedBy: actorUid || null,
          ...(existingOrgPrompt.exists
            ? {}
            : { createdBy: actorUid || null }),
        },
      },
      run: () =>
        orgPromptRef.set(
          {
            scope: SCOPES.ORG,
            scopeId: organizationId,
            promptId,
            layer: "organization",
            ...orgPromptFields,
            ...orgExtraFields,
            updatedBy: actorUid || null,
            updatedAt: now,
            ...(existingOrgPrompt.exists
              ? {}
              : { createdBy: actorUid || null, createdAt: now }),
          },
          { merge: true },
        ),
    });
  }

  if (
    regionalPromptRef &&
    regionalPrompt !== undefined &&
    !isGlobalAdmin
  ) {
    writeOps.push({
      ref: regionalPromptRef,
      historyPayload: buildHistoryPayload({
        changedValues: { regionalPrompt },
        previousData: existingRegional.exists ? existingRegional.data() : {},
        scope: SCOPES.REGION,
        scopeId: trimmedRegionId,
        promptId,
        reason,
      }),
      source: `${LLM_COLLECTIONS.LOCAL_PROMPTS}:${SCOPES.REGION}:${trimmedRegionId}:${promptId}`,
      debug: {
        action: existingRegional.exists
          ? "update"
          : existingRegional.__permissionDenied
            ? "create-or-update (pre-read denied)"
            : "create",
        path: getLocalPromptPath({
          scope: SCOPES.REGION,
          scopeId: trimmedRegionId,
          promptId,
        }),
        callerUserDoc: callerUserDocDebug,
        payload: {
          scope: SCOPES.REGION,
          scopeId: trimmedRegionId,
          promptId,
          layer: "regional",
          prompt: regionalPrompt,
          regionalPrompt,
          updatedBy: actorUid || null,
          ...(existingRegional.exists
            ? {}
            : { createdBy: actorUid || null }),
        },
      },
      run: () =>
        regionalPromptRef.set(
          {
            scope: SCOPES.REGION,
            scopeId: trimmedRegionId,
            promptId,
            layer: "regional",
            prompt: regionalPrompt,
            regionalPrompt,
            updatedBy: actorUid || null,
            updatedAt: now,
            ...(existingRegional.exists
              ? {}
              : { createdBy: actorUid || null, createdAt: now }),
          },
          { merge: true },
        ),
    });
  }

  const institutionIdForHierarchy =
    hierarchyInstitutionId || organizationId || null;

  const hierarchyLayers = [
    {
      level: 4,
      scopeId: p4ScopeId,
      canEdit: canEditP4,
      field: "subInstitutionPrompt",
      layerLabel: "sub-institution",
    },
    {
      level: 5,
      scopeId: p5ScopeId,
      canEdit: canEditP5,
      field: "departmentPrompt",
      layerLabel: "department",
    },
    {
      level: 6,
      scopeId: p6ScopeId,
      canEdit: canEditP6,
      field: "subDepartmentPrompt",
      layerLabel: "sub-department",
    },
  ];

  for (const layer of hierarchyLayers) {
    const trimmedScopeId =
      layer.scopeId && String(layer.scopeId).trim()
        ? String(layer.scopeId).trim()
        : null;
    if (!layer.canEdit || !trimmedScopeId) continue;
    if (typeof updates[layer.field] !== "string") continue;
    if (!institutionIdForHierarchy) {
      throw new Error(
        `Institution context is required to save ${layer.layerLabel} prompts.`,
      );
    }

    const hierarchyRef = getHierarchyPromptRef({
      level: layer.level,
      scopeId: trimmedScopeId,
      promptId,
    });
    const existingHierarchy = await getDocOrEmptyOnPermissionDenied(
      hierarchyRef.get(),
      `${LLM_COLLECTIONS.HIERARCHY_PROMPTS}:${layer.level}:${trimmedScopeId}:${promptId}`,
    );

    const promptText = updates[layer.field];
    const hierarchyPayload = {
      prompt: promptText,
      organizationPrompt: promptText,
    };

    writeOps.push({
      ref: hierarchyRef,
      historyPayload: buildHistoryPayload({
        changedValues: hierarchyPayload,
        previousData: existingHierarchy.exists ? existingHierarchy.data() : {},
        scope: SCOPES.HIERARCHY,
        scopeId: `${layer.level}__${trimmedScopeId}`,
        promptId,
        reason,
      }),
      source: `${LLM_COLLECTIONS.HIERARCHY_PROMPTS}:${layer.level}:${trimmedScopeId}:${promptId}`,
      debug: {
        action: existingHierarchy.exists ? "update" : "create",
        path: `${LLM_COLLECTIONS.HIERARCHY_PROMPTS}/${getHierarchyPromptDocId({
          level: layer.level,
          scopeId: trimmedScopeId,
          promptId,
        })}`,
        payload: {
          level: layer.level,
          scopeId: trimmedScopeId,
          promptId,
          institutionId: institutionIdForHierarchy,
          layer: layer.layerLabel,
          ...hierarchyPayload,
          updatedBy: actorUid || null,
          ...(existingHierarchy.exists ? {} : { createdBy: actorUid || null }),
        },
      },
      run: () =>
        hierarchyRef.set(
          {
            level: layer.level,
            scopeId: trimmedScopeId,
            promptId,
            institutionId: institutionIdForHierarchy,
            layer: layer.layerLabel,
            ...hierarchyPayload,
            updatedBy: actorUid || null,
            updatedAt: now,
            ...(existingHierarchy.exists
              ? {}
              : { createdBy: actorUid || null, createdAt: now }),
          },
          { merge: true },
        ),
    });
  }

  if (!writeOps.length) {
    throw new Error("No valid prompt updates were provided for your role.");
  }

  for (const op of writeOps) {
    try {
      await op.run();
    } catch (error) {
      error.promptRegistrySource = op.source;
      error.promptRegistryDebug = op.debug;
      throw error;
    }
  }

  // Write history entries (fire-and-forget; never block the save)
  const historyTimestamp = firebase.firestore.FieldValue.serverTimestamp();
  Promise.all(
    writeOps
      .filter((op) => op.ref && op.historyPayload)
      .map((op) =>
        op.ref
          .collection("history")
          .add({
            ...op.historyPayload,
            changedBy: actorUid || null,
            changedByEmail: actorEmail || null,
            changedAt: historyTimestamp,
          })
          .catch((err) =>
            console.warn("[promptRegistryService] history write failed", op.source, err),
          ),
      ),
  );

  const [freshQuery, freshGlobal, freshOrgPrompt, freshRegional] =
    await Promise.all([
      queryRef.get(),
      isGlobalAdmin
        ? globalRef.get()
        : Promise.resolve({ exists: false, data: () => ({}) }),
      orgPromptRef
        ? orgPromptRef.get()
        : Promise.resolve({ exists: false, data: () => ({}) }),
      regionalPromptRef
        ? regionalPromptRef.get()
        : Promise.resolve({ exists: false, data: () => ({}) }),
    ]);

  const freshHierarchy = await Promise.all(
    hierarchyLayers
      .filter((layer) => layer.scopeId && String(layer.scopeId).trim())
      .map(async (layer) => {
        const trimmedScopeId = String(layer.scopeId).trim();
        const ref = getHierarchyPromptRef({
          level: layer.level,
          scopeId: trimmedScopeId,
          promptId,
        });
        const snap = await ref.get();
        return { level: layer.level, snap };
      }),
  );

  const p4Data =
    freshHierarchy.find((row) => row.level === 4)?.snap?.exists
      ? freshHierarchy.find((row) => row.level === 4).snap.data()
      : {};
  const p5Data =
    freshHierarchy.find((row) => row.level === 5)?.snap?.exists
      ? freshHierarchy.find((row) => row.level === 5).snap.data()
      : {};
  const p6Data =
    freshHierarchy.find((row) => row.level === 6)?.snap?.exists
      ? freshHierarchy.find((row) => row.level === 6).snap.data()
      : {};

  return normalizePrompt(
    promptId,
    freshQuery.exists ? freshQuery.data() : {},
    freshOrgPrompt.exists ? freshOrgPrompt.data() : {},
    freshGlobal.exists ? freshGlobal.data() : {},
    freshRegional.exists ? freshRegional.data() : {},
    { ...p4Data, level: 4, scopeId: p4ScopeId || null },
    { ...p5Data, level: 5, scopeId: p5ScopeId || null },
    { ...p6Data, level: 6, scopeId: p6ScopeId || null },
  );
};

const getGlobalPrompt = async ({ promptId }) => {
  const doc = await db
    .collection(LLM_COLLECTIONS.GLOBAL_PROMPTS)
    .doc(promptId)
    .get();
  if (!doc.exists) return null;
  return {
    id: doc.id,
    ...doc.data(),
  };
};

const upsertGlobalPrompt = async ({
  promptId,
  systemPrompt,
  contextProvided,
  responseFormat,
  model,
  maxTokens,
  temperature,
  actorUid,
}) => {
  const ref = db.collection(LLM_COLLECTIONS.GLOBAL_PROMPTS).doc(promptId);
  const existing = await ref.get();
  const payload = {
    scope: SCOPES.GLOBAL,
    scopeId: "default",
    promptId,
    ...(typeof systemPrompt === "string" ? { systemPrompt } : {}),
    ...(typeof contextProvided === "string" ? { contextProvided } : {}),
    ...(typeof responseFormat === "string" ? { responseFormat } : {}),
    ...(typeof model === "string" ? { model } : {}),
    ...(typeof maxTokens === "number" ? { maxTokens } : {}),
    ...(typeof temperature === "number" ? { temperature } : {}),
    updatedBy: actorUid || null,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };
  if (!existing.exists) {
    payload.createdBy = actorUid || null;
    payload.createdAt = firebase.firestore.FieldValue.serverTimestamp();
  }
  await ref.set(payload, { merge: true });
  const fresh = await ref.get();
  return {
    id: fresh.id,
    ...fresh.data(),
  };
};

const createPrompt = async ({
  id,
  data,
  reason = null,
  actorUid,
  actorEmail = null,
  organizationId = null,
  regionId = null,
  isGlobalAdmin = false,
  p4ScopeId = null,
  p5ScopeId = null,
  p6ScopeId = null,
  canEditP4 = false,
  canEditP5 = false,
  canEditP6 = false,
  hierarchyInstitutionId = null,
}) => {
  const promptId = (id || "").trim();
  if (!promptId) {
    throw new Error("Prompt ID is required.");
  }
  return upsertPrompt({
    promptId,
    updates: data,
    reason,
    actorUid,
    actorEmail,
    organizationId,
    regionId,
    isGlobalAdmin,
    p4ScopeId,
    p5ScopeId,
    p6ScopeId,
    canEditP4,
    canEditP5,
    canEditP6,
    hierarchyInstitutionId,
  });
};

const updatePrompt = ({
  id,
  updates,
  reason = null,
  actorUid,
  actorEmail = null,
  organizationId = null,
  regionId = null,
  isGlobalAdmin = false,
  p4ScopeId = null,
  p5ScopeId = null,
  p6ScopeId = null,
  canEditP4 = false,
  canEditP5 = false,
  canEditP6 = false,
  hierarchyInstitutionId = null,
}) => {
  return upsertPrompt({
    promptId: id,
    updates,
    reason,
    actorUid,
    actorEmail,
    organizationId,
    regionId,
    isGlobalAdmin,
    p4ScopeId,
    p5ScopeId,
    p6ScopeId,
    canEditP4,
    canEditP5,
    canEditP6,
    hierarchyInstitutionId,
  });
};

const getPromptHistory = async ({
  promptId,
  scope = "global",
  scopeId = null,
  hierarchyLevel = null,
  limit = 20,
}) => {
  let ref;
  if (scope === "global") {
    ref = db.collection(LLM_COLLECTIONS.GLOBAL_PROMPTS).doc(promptId);
  } else if (scope === SCOPES.HIERARCHY && hierarchyLevel && scopeId) {
    ref = getHierarchyPromptRef({
      level: hierarchyLevel,
      scopeId,
      promptId,
    });
  } else {
    const docId = getLocalPromptDocId({ scope, scopeId, promptId });
    ref = db.collection(LLM_COLLECTIONS.LOCAL_PROMPTS).doc(docId);
  }
  const snap = await ref
    .collection("history")
    .orderBy("changedAt", "desc")
    .limit(limit)
    .get();
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

const promptRegistryService = {
  listAll,
  createPrompt,
  updatePrompt,
  getGlobalPrompt,
  upsertGlobalPrompt,
  getPromptHistory,
  toMillis,
};

export default promptRegistryService;
